import { useTranslate } from '../../../../../customHooks';
import {
  composeValidators,
  maxValue,
  minValue,
} from '../../../../../utils/validate';
import { FormLayout, Input, SubmitButton } from '@panwds/react-form';
import { Button, InputSuffix } from '@panwds/react-ui';
import { isNumberTypes } from '@panwds/react-filters';
import { useForm, FormProvider } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { toast } from '../../../../../components';
import { useUpdateFirewallMutation } from '../../../../../redux/services/firewalls-service';
import { extractFieldsWithConfig } from '../../../../../utils/utils';
import styles from '../../../FirewallEdit.module.scss';

interface GwlbTimeoutFieldProps {
  isDisabled: boolean;
  firewallData: Record<string, any>;
  refreshFirewallData: () => void;
  formButtonsAtBottom?: boolean;
}

const GwlbTimeoutField = ({
  isDisabled,
  firewallData,
  refreshFirewallData,
  formButtonsAtBottom = false,
}: GwlbTimeoutFieldProps) => {
  const translate = useTranslate();
  const region: any = new URLSearchParams(location.search).get('region');
  const [updateFirewall] = useUpdateFirewallMutation();

  const config = useMemo(() => {
    return {
      Firewall: ['FirewallId', 'UpdateToken', 'DeploymentUpdateToken'],
    };
  }, []);

  const transformFormDefaultValue = useMemo(() => {
    if (isEmpty(firewallData)) {
      return undefined;
    }
    const newStateWithConfig: any = extractFieldsWithConfig(
      firewallData,
      config
    );

    return {
      Firewall: {
        ...newStateWithConfig.Firewall,
        GwlbTcpIdleTimeout: String(
          firewallData.Firewall?.GWLB?.TCPIdleTimeout || 350
        ),
      },
    };
  }, [config, firewallData]);

  const transformFormSubmitValue = useCallback(
    (formData: Record<string, any>) => {
      if (isEmpty(formData)) {
        return;
      }

      return {
        ...formData.Firewall,
        Region: region,
        GwlbTcpIdleTimeout: parseInt(formData.Firewall.GwlbTcpIdleTimeout),
      };
    },
    [region]
  );

  const formMethods = useForm({
    defaultValues: transformFormDefaultValue,
    mode: 'onChange',
  });
  const {
    formState: { isDirty },
    reset,
  } = formMethods;

  useEffect(() => {
    if (!isEmpty(firewallData)) {
      reset(transformFormDefaultValue);
    }
  }, [firewallData, reset, transformFormDefaultValue]);

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const result = await updateFirewall({
          payload: transformFormSubmitValue(data),
        }).unwrap();
        if (result) {
          const uniqueToastId = `update-gwlb-timeout-${Date.now()}`;
          toast.success(
            translate('Update successful', { toastId: uniqueToastId })
          );
          refreshFirewallData();
        }
      } catch (error: any) {
        const uniqueToastId = `update-gwlb-timeout-${Date.now()}`;
        const errorMessage =
          error?.data?.message || error?.message || 'Update failed';
        toast.error(errorMessage, { toastId: uniqueToastId });
        reset();
      }
    },
    [
      refreshFirewallData,
      reset,
      transformFormSubmitValue,
      translate,
      updateFirewall,
    ]
  );

  const handleCancel = () => {
    reset(transformFormDefaultValue);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <div className={formButtonsAtBottom ? styles.fieldContainerWithBottomPadding : undefined}>
          <FormLayout>
            <div className={styles.fieldContainer}>
              <Input
                label={translate(`resources.firewallsV2.gwlbTimeout`)}
                name="Firewall.GwlbTcpIdleTimeout"
                dataMetrics="cloudngfw-firewall-edit-gwlbtcpidletimeout"
                description={translate(`resources.firewallsV2.gwlbTimeoutDescription`)}
                tooltip={translate(`resources.firewallsV2.gwlbTimeoutInputTooltip`)}
                disabled={isDisabled}
                size="md"
                min={60}
                max={3600}
                suffix={<InputSuffix value="Seconds" size="md" />}
                type="number"
                pattern="[1-9]+[0-9]*"
                register={{
                  required: true,
                  validate: (value: any) =>
                    composeValidators(
                      isNumberTypes,
                      minValue(60),
                      maxValue(3600)
                    )(parseInt(value)),
                }}
              />
            </div>
          </FormLayout>
        </div>
        <div
          className={formButtonsAtBottom ? styles.formFooterButtons : styles.inlineButtonsContainer}
        >
          <div className={styles.formFooterDivider} />
          <div className={styles.buttonGroup}>
            <Button
              onClick={handleCancel}
              disabled={!isDirty}
            >
              {translate(`generic.cancel`)}
            </Button>
            <SubmitButton disabled={!isDirty}>
              {translate(`generic.save`)}
            </SubmitButton>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default GwlbTimeoutField;
